import React, { useLayoutEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  DollarSign,
  Gift,
  Globe,
  Star,
  Mail,
  Package2,
  X,
} from "lucide-react";
import { GLOBAL_IMAGES } from "../assets/asset";
import { getTutorById, TUTORS } from "./tutorInfoData";
import { useSubscription } from "../context/SubscriptionContext";
import {
  PARENT_BOOKING_HUB_PATH,
  persistParentPostAuthRedirect,
} from "../features/parentFlow/parentFlow";
import { logout } from "../features/auth/user.reducer";
import {
  getTutorStatusBadge,
  NO_AVAILABILITY_BADGE,
} from "./tutorAvailabilityStatus";
import {
  getTeacherDisplayName,
  getTeacherIdentifier,
  getTeacherPhotoValue,
  normalizeAvailabilityCollection,
  normalizeTeacherCollection,
  teacherApi,
} from "../features/teacher/teacherApi";
import { formatTeacherYearLabels } from "../utils/teacherYears";
import { normalizeAudPriceForDisplay } from "../utils/audCurrency";
import { resolveApiImageUrl } from "../utils/imageUrl";
import { getYearsTaughtBadge } from "../utils/tutorYearsTaught";
import { mergePendingTutorSlotsFromStorage } from "../components/ClassBooking/pendingTutorAvailability";
import LogoutConfirmDialog from "../components/LogoutConfirmDialog";

const PARENT_EXPECTATION_POINTS = [
  {
    title: "Clear lesson focus",
    description:
      "Each session stays centred on the maths topic your child needs the most help with right now.",
  },
  {
    title: "Calm step-by-step support",
    description:
      "Tutors break down tricky questions into smaller parts so children can follow along with confidence.",
  },
  {
    title: "Simple next-step guidance",
    description:
      "Parents get a clear sense of what was covered and what their child can keep practising after the session.",
  },
];

const HOW_IT_WORKS_DESCRIPTION =
  "Every session is designed to make learning clear, structured, and effective. Tutors use guided examples, simple explanations, and step-by-step practice to help students build confidence and understanding.";

const HOW_IT_WORKS_POINTS = [
  "Personalised lessons based on your child's level",
  "Calm, supportive teaching style",
  "Practice-led approach to reinforce learning",
  "Seamless online sessions with parent-friendly communication",
];

const DEFAULT_TUTOR_BADGE = "Available for booking";
const PUBLIC_TUTOR_QUERY_STALE_TIME_MS = 5 * 60 * 1000;
const PUBLIC_TUTOR_DETAIL_FALLBACK_ERROR_MESSAGE =
  "We couldn't load this tutor profile right now. Please check your internet and try again.";

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isFutureAvailabilitySlot = (slot, now = new Date()) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
};

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="160" height="160" rx="32" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Inter, sans-serif" font-size="58" font-weight="400" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getTeacherYears = (teacher = {}) => {
  const yearSource =
    teacher?.year_labels?.length || typeof teacher?.year_labels === "string"
      ? teacher.year_labels
      : teacher?.year;

  return formatTeacherYearLabels(yearSource);
};

const getTeacherBio = (teacher = {}) =>
  [teacher?.bio, teacher?.about].find((value) => String(value || "").trim()) || "";

const formatTutorPriceValue = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "Price on request";
  }

  return `$${new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value)}/hour`;
};

const formatTutorPriceAmount = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "On request";
  }

  return `$${new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value)}`;
};

const formatTutorHourlyCompactValue = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "On request";
  }

  return `${formatTutorPriceAmount(value)}/h`;
};

const splitOnRequestText = (value = "") => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) {
    return [];
  }

  const match = trimmedValue.match(/^(.*?)(\s+on request)$/i);
  if (!match) {
    return [trimmedValue];
  }

  return [match[1].trim(), "on request"];
};

const getSimilarTutorExperienceYears = (tutor = {}) => {
  const directExperience = toNumber(tutor?.experience);
  if (directExperience !== null) {
    return directExperience;
  }

  const experienceMatch = String(tutor?.experienceLabel || "").match(/(\d+)/);
  if (!experienceMatch) {
    return null;
  }

  const parsedYears = Number(experienceMatch[1]);
  return Number.isFinite(parsedYears) ? parsedYears : null;
};

const getSimilarTutorSubtitle = (tutor = {}) => {
  const qualification = String(
    tutor?.qualificationLabel || tutor?.qualifications || ""
  ).trim();
  const experienceYears = getSimilarTutorExperienceYears(tutor);
  const experienceText = experienceYears !== null ? `${experienceYears}y Experience` : "";

  return [qualification, experienceText].filter(Boolean).join(" • ");
};

const getSimilarTutorMeta = (tutor = {}) => {
  const rating = toNumber(tutor?.rating);
  const reviews = toNumber(tutor?.reviews);

  if (rating !== null && reviews !== null) {
    return {
      kind: "review",
      text: `${rating.toFixed(1)} (${reviews} review${reviews === 1 ? "" : "s"})`,
    };
  }

  if (tutor?.availabilityLoaded) {
    if (!tutor?.hasAvailableSlots) {
      return null;
    }

    return {
      kind: "status",
      text: "Available",
    };
  }

  const fallbackStatus = String(
    tutor?.responseTime || tutor?.responseStat || tutor?.badge || ""
  ).trim();

  if (!fallbackStatus) {
    return null;
  }

  return {
    kind: "status",
    text: fallbackStatus,
  };
};

const shouldRetryPublicTutorQuery = (failureCount, error) => {
  if (failureCount >= 1) return false;
  if (!error || typeof error !== "object") return false;

  if (error.isTimeout || error.isNetworkError) return true;

  const status = Number(error.status);
  if (status === 0) return true;
  return status >= 500 && status <= 599;
};

const getPublicTutorDetailErrorMessage = (error) => {
  const message = String(error?.message || "").trim();
  if (!message) return PUBLIC_TUTOR_DETAIL_FALLBACK_ERROR_MESSAGE;

  if (/timeout|timed out|network error|failed to fetch|load failed/i.test(message)) {
    return PUBLIC_TUTOR_DETAIL_FALLBACK_ERROR_MESSAGE;
  }

  return message;
};

const buildBackendTutorModel = (teacher = {}) => {
  const teacherName = getTeacherDisplayName(teacher);
  const years = getTeacherYears(teacher);
  const bioText = getTeacherBio(teacher);
  const rawPrice = teacher?.price_per_session ?? teacher?.price;
  const price = normalizeAudPriceForDisplay(rawPrice);
  const experience = toNumber(teacher?.experience);
  const teacherImage =
    resolveApiImageUrl(getTeacherPhotoValue(teacher)) ||
    buildAvatarDataUri(teacherName);
  const lessonAudience =
    years.length > 0 ? years.join(", ") : "All listed year levels";
  const qualificationText = String(teacher?.qualifications || "").trim();
  const specializationText = String(teacher?.specializations || "").trim();
  const subjectText = String(teacher?.subjects || "").trim();
  const yearsText = years.length ? years.join(", ") : "school maths";
  const experienceLabel =
    experience !== null
      ? `Experience ${experience}+ year${experience === 1 ? "" : "s"}`
      : "";
  const qualificationLabel = qualificationText;
  const specializationLabel = specializationText;
  const formattedPrice = formatTutorPriceValue(price);
  const priceValue = typeof price === "number" ? price : "On request";

  return {
    id: getTeacherIdentifier(teacher) || teacherName.toLowerCase().replace(/\s+/g, "-"),
    name: teacherName,
    image: teacherImage,
    heroText: bioText,
    badge: DEFAULT_TUTOR_BADGE,
    years,
    topics: [
      years[0] || "Online tutoring",
      specializationText || "Homework support",
      subjectText || "Maths confidence",
      "Parent-friendly guidance",
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 4),
    about: bioText,
    parentFit:
      specializationText ||
      `A good fit for families looking for steady online maths support in ${yearsText}.`,
    supportIncludes: [
      qualificationText || "Step-by-step explanations",
      subjectText || "Weekly maths practice",
      specializationText || "Confidence building",
      experience !== null
        ? `${experience} year${experience === 1 ? "" : "s"} experience`
        : "Parent-friendly updates",
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 4),
    lessonAudience,
    lessonLanguage: "English",
    lessonOverview: [
      bioText,
      `Lessons focus on ${yearsText} with guided examples, calm explanations and practice that students can follow step by step.`,
      "Parents can expect clear communication, structured support and an online session format that keeps learning simple and accessible.",
    ].filter(Boolean),
    price: priceValue,
    packPrices: ["5h pack available on request", "10h pack available on request"],
    onlineRate: formattedPrice,
    freeLesson: "Free lesson availability on request",
    responseStat: "Fast reply",
    experience,
    specializationLabel,
    experienceLabel,
    qualificationLabel,
    students: toNumber(teacher?.students ?? teacher?.student_count) ?? 0,
    location: "Australia",
    mode: "Online",
    level: years.some((year) => Number(String(year).replace(/\D/g, "")) >= 7)
      ? "senior"
      : "primary",
  };
};

export default function TutorInfoDetailPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tutorId } = useParams();
  const { token } = useSelector((state) => state.user);
  const { hasAccess, checkSubscription } = useSubscription();
  const staticTutor = useMemo(() => getTutorById(tutorId), [tutorId]);
  const [similarPage, setSimilarPage] = useState(0);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showTutorEnquiryPopup, setShowTutorEnquiryPopup] = useState(false);
  const [bookingGateMessage, setBookingGateMessage] = useState("");
  const [isCheckingBookingAccess, setIsCheckingBookingAccess] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [tutorId]);

  const tutorDetailQuery = useQuery({
    queryKey: ["public-tutor-detail", tutorId],
    queryFn: () => teacherApi.getTutorDetail(tutorId),
    enabled: Boolean(tutorId) && !staticTutor,
    retry: shouldRetryPublicTutorQuery,
    staleTime: PUBLIC_TUTOR_QUERY_STALE_TIME_MS,
  });
  const tutorListQuery = useQuery({
    queryKey: ["public-tutor-info"],
    queryFn: () => teacherApi.getAllTeachers(),
    enabled: !staticTutor,
    retry: shouldRetryPublicTutorQuery,
    staleTime: PUBLIC_TUTOR_QUERY_STALE_TIME_MS,
  });
  const tutorAvailabilityQuery = useQuery({
    queryKey: ["public-tutor-availability", tutorId],
    queryFn: () => teacherApi.getTutorAvailability(tutorId),
    enabled: Boolean(tutorId) && !staticTutor && !tutorDetailQuery.isError,
    retry: shouldRetryPublicTutorQuery,
    staleTime: PUBLIC_TUTOR_QUERY_STALE_TIME_MS,
  });
  const backendTeachers = useMemo(
    () => normalizeTeacherCollection(tutorListQuery.data),
    [tutorListQuery.data]
  );
  const futureAvailabilitySlots = useMemo(() => {
    if (staticTutor) return [];

    return mergePendingTutorSlotsFromStorage({
      tutorId,
      availabilitySlots: normalizeAvailabilityCollection(
        tutorAvailabilityQuery.data
      ),
    }).filter((slot) => isFutureAvailabilitySlot(slot));
  }, [staticTutor, tutorAvailabilityQuery.data, tutorId]);
  const dynamicTutor = useMemo(
    () => {
      if (staticTutor || !tutorDetailQuery.data) return null;
      return buildBackendTutorModel(tutorDetailQuery.data);
    },
    [staticTutor, tutorDetailQuery.data]
  );
  const tutor = staticTutor || dynamicTutor;
  const isBookingUnavailable =
    !staticTutor &&
    tutorAvailabilityQuery.isSuccess &&
    futureAvailabilitySlots.length === 0;
  const tutorStatusBadge = getTutorStatusBadge({
    badge: tutor?.badge,
    isStaticTutor: Boolean(staticTutor),
    availabilityLoaded: tutorAvailabilityQuery.isSuccess,
    futureAvailabilityCount: futureAvailabilitySlots.length,
  });
  const yearsTaughtBadge = getYearsTaughtBadge(tutor?.years);
  const tutorTopBadges = [
    tutor?.specializationLabel || tutorStatusBadge,
    tutor?.experienceLabel || "",
    tutor?.qualificationLabel || "",
    yearsTaughtBadge,
  ].filter(Boolean);

  const similarDynamicTeachers = useMemo(() => {
    if (staticTutor) return [];

    return backendTeachers
      .filter((teacher) => {
        const teacherIdentifier = getTeacherIdentifier(teacher);
        return teacherIdentifier && teacherIdentifier !== tutorId;
      })
      .slice(0, 12);
  }, [backendTeachers, staticTutor, tutorId]);

  const similarTutorAvailabilityQueries = useQueries({
    queries: similarDynamicTeachers.map((teacher, index) => {
      const routeId = getTeacherIdentifier(teacher);

      return {
        queryKey: ["public-similar-tutor-availability", routeId || `teacher-${index}`],
        queryFn: () => teacherApi.getTutorAvailability(routeId),
        enabled: Boolean(routeId) && !staticTutor,
        retry: false,
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  const dynamicSimilarTutors = useMemo(
    () =>
      similarDynamicTeachers.map((teacher, index) => {
        const routeId = getTeacherIdentifier(teacher);
        const futureAvailabilityCount = mergePendingTutorSlotsFromStorage({
          tutorId: routeId,
          availabilitySlots: normalizeAvailabilityCollection(
            similarTutorAvailabilityQueries[index]?.data
          ),
        }).filter((slot) => isFutureAvailabilitySlot(slot)).length;

        return {
          ...buildBackendTutorModel(teacher),
          availabilityLoaded: similarTutorAvailabilityQueries[index]?.isSuccess,
          hasAvailableSlots:
            similarTutorAvailabilityQueries[index]?.isSuccess &&
            futureAvailabilityCount > 0,
          futureAvailabilityCount,
        };
      }),
    [similarDynamicTeachers, similarTutorAvailabilityQueries]
  );

  const similarTutors = (staticTutor
    ? TUTORS.filter((candidateTutor) => candidateTutor.id !== tutor.id).sort(
        (firstTutor, secondTutor) => {
          if (firstTutor.level === tutor.level && secondTutor.level !== tutor.level) return -1;
          if (firstTutor.level !== tutor.level && secondTutor.level === tutor.level) return 1;
          return 0;
        }
      )
    : dynamicSimilarTutors);
  const similarTutorsPerPage = 3;
  const totalSimilarPages = Math.max(
    1,
    Math.ceil(similarTutors.length / similarTutorsPerPage)
  );
  const visibleSimilarTutors = similarTutors.slice(
    similarPage * similarTutorsPerPage,
    similarPage * similarTutorsPerPage + similarTutorsPerPage
  );

  if (!tutor && tutorDetailQuery.isLoading) {
    return (
      <div className="min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-10 sm:px-6 lg:px-8">
          <div className="rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-16 text-center text-lg font-medium text-[#475569]">
            Loading tutor profile...
          </div>
        </div>
      </div>
    );
  }

  if (!tutor && tutorDetailQuery.isError) {
    return (
      <div className="min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-10 sm:px-6 lg:px-8">
          <div className="rounded-[34px] border border-[#f1c8bf] bg-[#fff7f5] px-6 py-16 text-center">
            <p className="text-xl font-medium text-[#f97316]">
              We couldn't load this tutor profile.
            </p>
            <p className="mt-3 text-base text-[#475569]">
              {getPublicTutorDetailErrorMessage(tutorDetailQuery.error)}
            </p>
            <Link
              to="/tutorinfo"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#FF5E47] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
            >
              Back to tutors
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleParentAuthNavigation = (path) => {
    persistParentPostAuthRedirect(PARENT_BOOKING_HUB_PATH);
    navigate(path, {
      state: {
        redirectTo: PARENT_BOOKING_HUB_PATH,
      },
    });
  };

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutDialog(false);
    dispatch(logout());
    navigate("/tutorinfo", { replace: true });
  };

  const handleTutorEnquiryOpen = () => {
    setShowTutorEnquiryPopup(true);
  };

  const handleTutorEnquiryClose = () => {
    setShowTutorEnquiryPopup(false);
  };

  const handleBookSession = async () => {
    setBookingGateMessage("");
    persistParentPostAuthRedirect(PARENT_BOOKING_HUB_PATH);

    if (!token) {
      setShowAuthPrompt(true);
      return;
    }

    let canBook = hasAccess;
    setIsCheckingBookingAccess(true);

    if (!canBook) {
      try {
        const latestSubscription = await checkSubscription();
        const latestStatus = latestSubscription?.subscription_status;
        const requiresPaymentMethod = Boolean(
          latestSubscription?.requires_payment_method
        );

        canBook =
          Boolean(latestSubscription?.has_access) ||
          ((latestStatus === "active" || latestStatus === "trialing") &&
            !requiresPaymentMethod);
      } catch (_error) {
        setBookingGateMessage(
          "We couldn't verify the student's subscription right now. Please try again in a moment."
        );
        setIsCheckingBookingAccess(false);
        return;
      }
    }

    setIsCheckingBookingAccess(false);

    if (canBook) {
      navigate(PARENT_BOOKING_HUB_PATH);
      return;
    }

    navigate("/subscription", {
      state: {
        from: PARENT_BOOKING_HUB_PATH,
        source: "parent-booking",
      },
    });
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
      <div className="mx-auto max-w-7xl px-4 pb-12 pt-6 sm:px-6 lg:px-8">
        <header className="border-b border-[#ddd3c6] pb-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <Link className="flex items-center gap-3" to="/tutorinfo">
              <img
                src={GLOBAL_IMAGES.dingo}
                alt="MathDingo"
                className="h-14 w-14 object-contain sm:h-16 sm:w-16"
              />
              <img
                src={GLOBAL_IMAGES.dingoLogoText}
                alt="MathDingo"
                className="h-10 w-auto object-contain sm:h-12"
              />
            </Link>

            <div className="flex flex-wrap items-center gap-3 xl:justify-end">
              <Link
                to="/contact"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF5E47] bg-white text-[#FF5E47]"
                aria-label="Contact support"
              >
                <Mail size={18} />
              </Link>
              <button
                type="button"
                onClick={handleTutorEnquiryOpen}
                className="rounded-2xl border border-[#FF5E47] bg-white px-5 py-2.5 text-sm font-medium text-[#FF5E47] sm:text-base"
              >
                Become a Tutor
              </button>
              {token ? (
                <>
                  <Link
                    to="/parent/classBooking/myBookings"
                    className="rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                  >
                    My Bookings
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-2xl border border-[#FF5E47] bg-white px-5 py-2.5 text-sm font-medium text-[#FF5E47] transition hover:bg-[#fff4f1] sm:text-base"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/parent/login"
                  className="rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                >
                  Parent Log In
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="pt-8">
          <Link
            to="/tutorinfo"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#FF5E47] transition hover:opacity-80"
          >
            <ArrowLeft size={18} />
            Back to tutors
          </Link>

          <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,1fr)_292px] lg:items-start">
            <section>
              <h2 className="text-3xl font-medium leading-tight text-[#111827] sm:text-4xl">
                About {tutor.name}
              </h2>

              <p className="mt-6 max-w-[640px] font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]">
                {tutor.heroText}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                {tutorTopBadges.map((badge, index) => (
                  <div
                    key={`${tutor.id}-status-${index}`}
                    className="inline-flex w-fit max-w-full items-center gap-2 rounded-2xl border border-[#f8c8bf] bg-[#fff4f1] px-3 py-1.5 text-center font-inter text-xs font-semibold text-[#ff5a3d] sm:px-4 sm:py-2 sm:text-sm"
                  >
                    <span>{badge}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  What parents can expect
                </h2>

                <div className="mt-6 space-y-4">
                  {PARENT_EXPECTATION_POINTS.map((point) => (
                    <div
                      key={point.title}
                      className="flex items-start gap-4 rounded-[28px] border border-[#ddd3c6] bg-white px-5 py-4"
                    >
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#59c47a] text-white">
                        <Check
                          size={12}
                          strokeWidth={3}
                        />
                      </span>

                      <div>
                        <h3 className="text-base font-normal text-[#111827] sm:text-lg">
                          {point.title}
                        </h3>
                        <p
                          className="mt-1 font-sans text-sm leading-6 text-[#5f6776] sm:text-base"
                          style={{ fontWeight: 400 }}
                        >
                          {point.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-16">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  How it works
                </h2>

                <div className="mt-6">
                  <p className="max-w-6xl font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]">
                    {HOW_IT_WORKS_DESCRIPTION}
                  </p>

                  <div className="mt-8 space-y-3">
                    {HOW_IT_WORKS_POINTS.map((point) => (
                      <div
                        key={point}
                        className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-3"
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#dff8e6]">
                          <span className="h-2.5 w-2.5 rounded-full bg-[#54b948]" />
                        </span>
                        <span
                          className="font-sans text-sm leading-6 text-[#5f6776] sm:text-base"
                          style={{ fontWeight: 400 }}
                        >
                          {point}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-16">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  Rates
                </h2>

                <div className="mt-6 rounded-[28px] border border-[#ddd3c6] bg-white px-4 py-5 sm:px-5 sm:py-6">
                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <div className="flex items-center gap-3">
                      <DollarSign size={24} strokeWidth={1.9} className="text-[#ff6b00]" />
                      <p className="text-base font-medium text-[#3f3f46] sm:text-lg">
                        Price
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Package2 size={24} strokeWidth={1.9} className="text-[#ff6b00]" />
                      <p className="text-base font-medium text-[#3f3f46] sm:text-lg">
                        Pack prices
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Globe size={24} strokeWidth={1.9} className="text-[#ff6b00]" />
                      <p className="text-base font-medium text-[#3f3f46] sm:text-lg">
                        Online
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Gift
                        size={24}
                        strokeWidth={1.9}
                        className="text-[#ff6b00]"
                      />
                      <p className="text-base font-medium text-[#3f3f46] sm:text-lg">
                        Free lessons
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 border-t border-[#e6dfd5]" />

                  <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <div className="pl-9 md:min-h-[104px]">
                      <p className="text-base font-medium text-[#111827] sm:text-lg">
                        {formatTutorPriceAmount(tutor.price)}
                      </p>
                    </div>

                    <div className="space-y-4 md:min-h-[104px]">
                      {tutor.packPrices.map((pack) => {
                        const packLines = splitOnRequestText(pack);
                        return (
                          <div
                            key={`${tutor.id}-${pack}`}
                            className="max-w-[220px] text-[#111827]"
                          >
                            {packLines.map((line, index) => (
                              <p
                                key={`${pack}-${line}`}
                                className={
                                  index === 0
                                    ? "text-base font-medium leading-[1.45] sm:text-lg"
                                    : "mt-1 font-sans text-base leading-[1.55] text-[#5f6776] sm:text-lg"
                                }
                                style={index === 0 ? undefined : { fontWeight: 400 }}
                              >
                                {line}
                              </p>
                            ))}
                          </div>
                        );
                      })}
                    </div>

                    <div className="pl-9 md:min-h-[104px]">
                      <p className="text-base font-medium leading-[1.45] text-[#111827] sm:text-lg">
                        {formatTutorHourlyCompactValue(tutor.price)}
                      </p>
                    </div>

                    <div className="pl-9 md:min-h-[104px]">
                      <p className="max-w-[230px] text-base font-medium leading-[1.45] text-[#111827] sm:text-lg">
                        {tutor.freeLesson}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-[#f1ece4]" />

                  <div className="mt-6 rounded-[20px] border border-[#cfe0ff] bg-[#edf5ff] px-4 py-4 sm:px-5 sm:py-[18px]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-[#dbe8ff] text-[#2b5fff]">
                        <Package2 size={22} strokeWidth={1.8} />
                      </div>

                      <div className="max-w-4xl">
                        <h3 className="text-base font-medium leading-tight text-[#27439b] sm:text-lg">
                          Save with lesson packs
                        </h3>
                        <p
                          className="mt-3 font-sans text-base leading-[1.55] text-[#27439b] sm:text-lg"
                          style={{ fontWeight: 400 }}
                        >
                          Purchase multiple lessons upfront for a discounted rate.
                          {" "}Contact{" "}
                          <Link
                            to="/contact"
                            className="font-semibold text-[#27439b] transition hover:underline"
                          >
                            Mathdingo
                          </Link>{" "}
                          to discuss pack options.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-16 pb-2">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                    Explore similar Maths tutors in Australia 
                  </h2>

                  <div className="flex items-center gap-3 self-start sm:self-auto">
                    <span
                      className="font-sans text-lg text-[#111827]"
                      style={{ fontWeight: 400 }}
                    >
                      {Math.min(similarPage + 1, totalSimilarPages)}/{totalSimilarPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setSimilarPage((currentPage) => Math.max(0, currentPage - 1))
                      }
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#FF5E47] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={similarPage === 0}
                      aria-label="Previous similar tutors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSimilarPage((currentPage) =>
                          Math.min(totalSimilarPages - 1, currentPage + 1)
                        )
                      }
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#FF5E47] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={similarPage >= totalSimilarPages - 1}
                      aria-label="Next similar tutors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleSimilarTutors.map((similarTutor) => {
                    const similarTutorMeta = getSimilarTutorMeta(similarTutor);
                    const similarTutorSubtitle = getSimilarTutorSubtitle(similarTutor);

                    return (
                      <article
                        key={similarTutor.id}
                        className="rounded-[30px] border border-[#f0ddd5] bg-[linear-gradient(180deg,#fff9f7_0%,#fff5ef_100%)] p-5 shadow-[0_18px_40px_rgba(255,94,71,0.08)] transition hover:-translate-y-1 hover:shadow-[0_22px_46px_rgba(255,94,71,0.12)]"
                      >
                      <Link
                        to={`/tutorinfo/${similarTutor.id}`}
                        className="group block"
                      >
                        <div className="overflow-hidden rounded-[26px] border border-[#f5e3dc] bg-white">
                          <img
                            src={similarTutor.image}
                            alt={similarTutor.name}
                            className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        </div>
                      </Link>

                      <div className="mt-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 overflow-hidden">
                            <h3 className="shrink-0 text-[21px] font-medium leading-none text-[#231815] sm:text-[22px]">
                              {similarTutor.name}
                            </h3>
                            {similarTutorSubtitle ? (
                              <p className="mt-2 truncate font-sans text-[13px] font-normal leading-none text-[#6e635d] sm:text-sm">
                                {similarTutorSubtitle}
                              </p>
                            ) : null}
                          </div>

                          <div className="shrink-0 flex items-baseline gap-1 pl-2 text-right">
                            <p className="text-[21px] font-medium leading-none text-[#c65b2c] sm:text-[22px]">
                              {formatTutorPriceAmount(similarTutor.price)}
                            </p>
                            <p className="font-sans text-xs font-normal leading-none text-[#c65b2c] sm:text-[13px]">
                              /hour
                            </p>
                          </div>
                        </div>

                        {similarTutorMeta ? (
                          <div className="mt-4 flex items-center gap-2 font-sans text-sm font-normal leading-[1.5] text-[#5f6776] sm:text-base">
                            {similarTutorMeta.kind === "review" ? (
                              <Star
                                size={17}
                                className="shrink-0 fill-[#c65b2c] text-[#c65b2c]"
                              />
                            ) : (
                              <Clock3 size={17} className="shrink-0 text-[#c65b2c]" />
                            )}
                            <span>{similarTutorMeta.text}</span>
                          </div>
                        ) : null}
                      </div>
                      </article>
                    );
                  })}
                </div>
              </div>

            </section>

            <aside className="lg:sticky lg:top-6">
              <div className="rounded-[30px] bg-white p-4 shadow-[0_20px_48px_rgba(0,0,0,0.08)]">
                <div className="mt-1 flex flex-col items-center text-center">
                  <img
                    src={tutor.image}
                    alt={tutor.name}
                    className="h-36 w-36 rounded-full border-4 border-[#fff4f1] object-cover shadow-[0_12px_30px_rgba(255,94,71,0.14)] sm:h-40 sm:w-40"
                  />

                  <h2 className="mt-4 text-[28px] font-medium leading-none text-[#111827]">
                    {tutor.name}
                  </h2>
                </div>

                <div className="mt-6 space-y-4 text-sm sm:text-base">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-[#374151]">Price</span>
                    <span className="flex items-baseline gap-1">
                      <span className="text-xl font-medium text-[#111827]">
                        {formatTutorPriceAmount(tutor.price)}
                      </span>
                      <span
                        className="font-sans text-sm leading-6 text-[#5f6776] sm:text-base"
                        style={{ fontWeight: 400 }}
                      >
                        /hour
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span
                      className="inline-flex items-center gap-2 font-sans text-sm leading-6 text-[#5f6776] sm:text-base"
                      style={{ fontWeight: 400 }}
                    >
                      <Clock3 size={16} className="text-[#FF5E47]" />
                      Fast Response
                    </span>
                    <span
                      className="font-sans text-sm leading-6 text-[#5f6776] sm:text-base"
                      style={{ fontWeight: 400 }}
                    >
                      {tutor.responseStat === "Fast reply"
                        ? "~ 2 hours"
                        : tutor.responseStat}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBookSession}
                  disabled={isCheckingBookingAccess || isBookingUnavailable}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-[#FF5E47] px-6 py-3 text-sm font-medium text-white transition hover:bg-[#e64d37] disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
                >
                  {isCheckingBookingAccess
                    ? "Checking access..."
                    : isBookingUnavailable
                      ? NO_AVAILABILITY_BADGE
                      : "Book Session"}
                </button>

                {!isBookingUnavailable ? (
                  <p
                    className={`mt-3 inline-flex w-full items-center justify-center gap-2 font-sans text-sm leading-6 sm:text-base ${
                      tutorStatusBadge === DEFAULT_TUTOR_BADGE
                        ? "text-[#45b665]"
                        : "text-[#ff6b61]"
                    }`}
                    style={{ fontWeight: 400 }}
                  >
                    {tutorStatusBadge === DEFAULT_TUTOR_BADGE ? (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#59c47a] text-white">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    ) : null}
                    {tutorStatusBadge}
                  </p>
                ) : null}
                <p
                  className="mt-2 text-center font-sans text-[11px] leading-4 text-[#5f6776] sm:text-xs"
                  style={{ fontWeight: 400 }}
                >
                  Parent account required before booking a MathDingo tutoring session.
                </p>
                {bookingGateMessage ? (
                  <div className="mt-4 rounded-[24px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {bookingGateMessage}
                  </div>
                ) : null}
              </div>
            </aside>
          </div>

          {showAuthPrompt ? (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#111827]/40 px-4 backdrop-blur-sm">
              <div className="relative w-full max-w-md rounded-[32px] border border-[#ddd3c6] bg-white p-6 shadow-[0_24px_70px_rgba(11,43,102,0.18)]">
                <button
                  type="button"
                  onClick={() => setShowAuthPrompt(false)}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#e5ddd0] text-[#FF5E47]"
                  aria-label="Close login prompt"
                >
                  <X size={18} />
                </button>

                <span className="inline-flex rounded-full bg-[#fff0ec] px-4 py-2 text-sm font-medium text-[#ff6b61]">
                  Parent login required
                </span>
                <h2 className="mt-5 text-3xl font-medium text-[#f97316]">
                  You&apos;re not logged in
                </h2>
                <p className="mt-3 text-base leading-7 text-[#475569]">
                  Please log in with the student&apos;s existing credentials, or
                  create a parent account first. We&apos;ll resume the booking
                  flow as soon as you&apos;re authenticated.
                </p>

                <div className="mt-8 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => handleParentAuthNavigation("/parent/login")}
                    className="inline-flex items-center justify-center rounded-2xl bg-[#FF5E47] px-5 py-3 text-base font-medium text-white transition hover:bg-[#e64d37]"
                  >
                    Parent Login
                  </button>
                  <button
                    type="button"
                    onClick={() => handleParentAuthNavigation("/parent/signup")}
                    className="inline-flex items-center justify-center rounded-2xl border border-[#FF5E47] bg-white px-5 py-3 text-base font-medium text-[#FF5E47] transition hover:bg-[#fff4f1]"
                  >
                    Create a Parent Account
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <LogoutConfirmDialog
            open={showLogoutDialog}
            onCancel={() => setShowLogoutDialog(false)}
            onConfirm={handleLogoutConfirm}
            tone="coral"
          />
          {showTutorEnquiryPopup ? (
            <div className="fixed inset-0 z-[140] flex items-center justify-center bg-[#111827]/40 px-4 backdrop-blur-sm">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="tutor-enquiry-title"
                className="w-full max-w-md rounded-[32px] border border-[#ddd3c6] bg-white p-6 shadow-[0_24px_70px_rgba(11,43,102,0.18)]"
              >
                <h2
                  id="tutor-enquiry-title"
                  className="text-center text-3xl font-medium text-[#FF5E47]"
                >
                  Become a Teacher
                </h2>
                <p className="mt-4 text-center font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]">
                  In order to become a teacher, please email us at{" "}
                  <span className="font-medium text-[#1b1b1b]">
                    support@mathdingo.com
                  </span>
                  .
                </p>

                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={handleTutorEnquiryClose}
                    className="inline-flex items-center justify-center rounded-2xl bg-[#FF5E47] px-6 py-3 text-base font-medium text-white transition hover:bg-[#e64d37]"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <footer className="mt-20 border-t border-[#ddd3c6] py-14">
            <div className="grid gap-y-12 md:grid-cols-2 md:gap-x-16 lg:grid-cols-4 lg:gap-x-12 xl:gap-x-20">
                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">About</h3>
                  <div className="mt-6 space-y-5">
                    <Link
                      className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] transition-colors hover:text-[#FF5E47] sm:text-lg"
                      to="/about"
                    >
                      About MathDingo
                    </Link>
                    <Link
                      className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] transition-colors hover:text-[#FF5E47] sm:text-lg"
                      to={token ? "/tutorinfo" : "/parent/login"}
                    >
                      Parent account
                    </Link>
                    {token ? (
                      <Link
                        className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] transition-colors hover:text-[#FF5E47] sm:text-lg"
                        to="/parent/classBooking/myBookings"
                      >
                        My bookings
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleTutorEnquiryOpen}
                      className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] transition-colors hover:text-[#FF5E47] sm:text-lg"
                    >
                      Become a tutor
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">
                    Learning areas
                  </h3>
                  <div className="mt-6 space-y-5">
                    {[
                      "Maths",
                      "Primary numeracy",
                      "Fractions",
                      "Algebra",
                      "Problem solving",
                      "Homework help",
                      "Exam preparation",
                      "Online tutoring",
                    ].map((item) => (
                      <span
                        key={item}
                        className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] sm:text-lg"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">Cities</h3>
                  <div className="mt-6 space-y-5">
                    {[
                      "Sydney",
                      "Melbourne",
                      "Brisbane",
                      "Perth",
                      "Adelaide",
                      "Canberra",
                      "Geelong",
                      "All cities",
                    ].map((city) => (
                      <span
                        key={city}
                        className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] sm:text-lg"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">
                    Help & support
                  </h3>
                  <div className="mt-6 space-y-5">
                    <Link
                      className="block font-sans text-base font-normal leading-[1.55] text-[#5f6776] transition-colors hover:text-[#FF5E47] sm:text-lg"
                      to="/contact"
                    >
                      Contact us
                    </Link>
                  </div>
                </div>
              </div>

            <div className="mt-12 border-t border-[#ddd3c6]">
              <div className="pt-6">
                <p className="font-sans text-lg font-normal leading-[1.55] text-[#5f6776] sm:text-xl">
                  © 2026 MathDingo, learn with confidence!
                </p>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
